// The register: every experiment the registry knows, as a CONTROL surface.
//
// It was a report, and a narrow one: the default filter dropped everything that
// was not mid-split, the registry called a shipped section `idle`, and the page
// rendered one row out of ninety-seven. The control for a row lived on another
// page, so moving a dial cost a navigation. Both are fixed here, and the two
// fixes are one idea: this page is the superset of the Prompt sections console,
// for all six kinds. Nothing is hidden by default, and the control is ON the row.
//
// The shape that makes a hundred rows readable: groups by kind, each with a
// count and a state breakdown in its header, collapsible, and every row that is
// currently SPLITTING somebody pinned to the top as its own group. "What is live
// right now" is still the first question; it just no longer costs the answer to
// "what else is there".
//
// Every rule about WHICH rows appear, in WHAT order and in WHICH group lives in
// `lib/experimentation.ts` and is unit-tested there, because a filter that
// silently drops a running experiment is a bug you cannot see by looking at the
// screen. This file renders what those functions return and nothing else.
//
// Two conventions carried from the rest of the console: an UNMEASURED value
// prints "-", never 0; and a control that moves live traffic asks for a reason
// before it does anything.
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";

import { Badge, Button } from "@recruiter/shared";
import type {
  AdminExperimentRecord,
  AdminExperimentationResponse,
  BadgeTone,
} from "@recruiter/shared";

import {
  DEFAULT_REGISTER_FILTERS,
  REGISTER_STATES,
  SAVED_VIEWS,
  SAVED_VIEW_LABELS,
  SPLITS_GROUP,
  agentsIn,
  allocatableKind,
  applySavedView,
  breachedKeys,
  daysRunningOf,
  decisionOf,
  expiryState,
  filterRegister,
  formatCostPerCall,
  formatLatencyMs,
  formatRate,
  groupRegister,
  highlightParts,
  lastChangeOf,
  nextSort,
  registerCsv,
  registerCsvFilename,
  registerFactsOf,
  registerSearchFrom,
  savedViewFilters,
  sortRegister,
  stateOf,
  summariseAllocation,
  todayIso,
  totalExposuresOf,
  type RegisterFilters,
  type RegisterGroup,
  type RegisterSearch,
  type RegisterSort,
  type RegisterSortKey,
  type SavedView,
} from "../lib/experimentation";
import { formatDateTime } from "../lib/format";
import { usePersistedState } from "../lib/persisted-state";
import { AllocationEditor } from "./experiment-allocation";
import { ExperimentInspector } from "./experiment-inspector";
import { shippedArm } from "./restore-defaults";
import "./experiment-register.css";

/** The tone each state prints in.
 *
 *  `shipped` and `off` are deliberately different tones, because they are
 *  opposite facts that used to share the word `idle`: shipped is the behaviour
 *  everybody gets, off is nobody outside the baseline. `unknown` is a warning
 *  rather than a neutral: a row whose lifecycle could not be read is a gap in
 *  the board, not a quiet experiment. */
const STATE_TONE: Record<string, BadgeTone> = {
  split: "success",
  canary: "warning",
  shipped: "accent",
  promoted: "success",
  decided: "info",
  killed: "danger",
  off: "neutral",
  unknown: "warning",
};

type Column = { key: RegisterSortKey; label: string; numeric?: boolean };

const COLUMNS: Column[] = [
  { key: "key", label: "Key" },
  { key: "kind", label: "Kind" },
  { key: "unit", label: "Unit" },
  { key: "state", label: "State" },
  { key: "allocation", label: "Arms" },
  { key: "exposures", label: "Exposures", numeric: true },
  { key: "cost", label: "Cost/call", numeric: true },
  { key: "errors", label: "Errors", numeric: true },
  { key: "p95", label: "p95", numeric: true },
  { key: "days", label: "Days", numeric: true },
  { key: "owner", label: "Owner" },
  { key: "expires", label: "Expires" },
  { key: "monday", label: "Monday" },
  { key: "lastChange", label: "Last change" },
];

/** How many columns a full-width row (the expanded control) has to span. Two
 *  fixed extras beside `COLUMNS`: guardrails and actions, plus the expander and
 *  the select box when the operator can act. */
function spanOf(canAct: boolean): number {
  return COLUMNS.length + 3 + (canAct ? 1 : 0);
}

/** Pins are per operator and per browser, not a fact about the experiment: this
 *  is "I am watching this one", not "I own this one". Ownership comes from the
 *  declaration and is what the "mine" view filters on. */
const PINS_KEY = "recruiter_admin_app_experiment_pins";

/** Which groups the operator has collapsed, remembered per browser. Collapsed
 *  is a reading preference, not a filter: a collapsed group still counts in its
 *  own header, so nothing disappears the way the old default filter made it. */
const COLLAPSED_KEY = "recruiter_admin_app_experiment_groups_collapsed";

type Props = {
  data: AdminExperimentationResponse;
  filters: RegisterFilters;
  sort: RegisterSort;
  view: SavedView | undefined;
  me: string;
  /** May this operator kill an experiment or move a dial? `system_admin` at full. */
  canAct: boolean;
  onSearchChange: (next: RegisterSearch) => void;
  onKill: (keys: readonly AdminExperimentRecord[], reason: string) => Promise<void>;
};

export function ExperimentRegister({
  data,
  filters,
  sort,
  view,
  me,
  canAct,
  onSearchChange,
  onKill,
}: Props) {
  const [pins, setPins] = usePersistedState<string[]>(PINS_KEY, []);
  const [collapsed, setCollapsed] = usePersistedState<string[]>(COLLAPSED_KEY, []);
  const [selected, setSelected] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [killing, setKilling] = useState<AdminExperimentRecord[] | null>(null);

  const ctx = useMemo(
    () => ({ breachedKeys: breachedKeys(data), now: Date.now(), me }),
    [data, me],
  );

  const rows = useMemo(() => {
    const viewed = applySavedView(data.experiments, view, ctx);
    return sortRegister(filterRegister(viewed, filters, ctx), sort);
  }, [data.experiments, view, filters, ctx, sort]);

  const groups = useMemo(() => groupRegister(rows), [rows]);

  const pinned = useMemo(() => new Set(pins), [pins]);
  const shut = useMemo(() => new Set(collapsed), [collapsed]);
  const push = (next: Partial<RegisterFilters>, nextView = view) =>
    onSearchChange(registerSearchFrom({ ...filters, ...next }, sort, nextView));

  const selectedRows = rows.filter((r) => selected.includes(r.key));

  return (
    <section className="exr" aria-labelledby="exr-title">
      <header className="exr__head">
        <h2 className="exp-section__title" id="exr-title">
          Register
        </h2>
        <p className="exp-section__sub">
          {rows.length.toLocaleString()} of {data.experiments.length.toLocaleString()} shown.
          Everything the registry derives is listed, grouped by kind, with whatever is currently
          splitting the population pinned to the top. Open a row to move its dial. A value nobody
          has measured prints as a dash, never as a zero.
        </p>
      </header>

      <SavedViews
        active={view}
        onPick={(next) =>
          onSearchChange(
            registerSearchFrom(
              next ? savedViewFilters(next, me) : DEFAULT_REGISTER_FILTERS,
              sort,
              next,
            ),
          )
        }
      />
      <FilterBar
        filters={filters}
        experiments={data.experiments}
        onChange={(next) => push(next)}
        onClear={() => onSearchChange({})}
      />

      <div className="exr__actions">
        <Button
          variant="outline"
          onClick={() => downloadCsv(registerCsv(rows, ctx))}
          disabled={rows.length === 0}
        >
          Export this view
        </Button>
        {canAct ? (
          <Button
            variant="outline"
            onClick={() => setKilling(selectedRows)}
            disabled={selectedRows.length === 0}
          >
            Kill {selectedRows.length || ""} selected
          </Button>
        ) : null}
        {selected.length > 0 ? (
          <Button variant="ghost" onClick={() => setSelected([])}>
            Clear selection
          </Button>
        ) : null}
      </div>

      {killing ? (
        <KillPanel
          rows={killing}
          onCancel={() => setKilling(null)}
          onConfirm={async (reason) => {
            await onKill(killing, reason);
            setKilling(null);
            setSelected([]);
          }}
        />
      ) : null}

      {rows.length === 0 ? (
        <p className="exp-section__empty">
          Nothing matches this view. Clear the filters to see the whole register.
        </p>
      ) : (
        <div className="exp-table-wrap">
          <table className="exp-table exr__table">
            <thead>
              <tr>
                <th scope="col" className="exr__pick" />
                {canAct ? <th scope="col" className="exr__pick" /> : null}
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={col.numeric ? "exp-table__num" : undefined}
                    aria-sort={
                      sort.key === col.key
                        ? sort.dir === "asc"
                          ? "ascending"
                          : "descending"
                        : "none"
                    }
                  >
                    <button
                      type="button"
                      className="exr__sort"
                      onClick={() =>
                        onSearchChange(
                          registerSearchFrom(filters, nextSort(sort, col.key), view),
                        )
                      }
                    >
                      {col.label}
                      {sort.key === col.key ? (
                        <span aria-hidden="true">{sort.dir === "asc" ? " ^" : " v"}</span>
                      ) : null}
                    </button>
                  </th>
                ))}
                <th scope="col">Guardrails</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            {groups.map((group) => (
              <GroupBody
                key={group.id}
                group={group}
                open={!shut.has(group.id)}
                canAct={canAct}
                query={filters.q}
                ctx={ctx}
                pinned={pinned}
                selected={selected}
                expanded={expanded}
                onToggleGroup={() =>
                  setCollapsed((prev) =>
                    prev.includes(group.id)
                      ? prev.filter((g) => g !== group.id)
                      : [...prev, group.id],
                  )
                }
                onExpand={(key) => setExpanded((prev) => (prev === key ? null : key))}
                onSelect={(key, on) =>
                  setSelected((prev) => (on ? [...prev, key] : prev.filter((k) => k !== key)))
                }
                onPin={(key) =>
                  setPins((prev) =>
                    prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
                  )
                }
                onKill={(row) => setKilling([row])}
              />
            ))}
          </table>
        </div>
      )}
    </section>
  );
}

/** One group: a header row that is a button, then its rows while it is open.
 *  A `<tbody>` per group rather than a nested table, so one set of columns lines
 *  up across every group and the sort headers keep meaning what they say. */
function GroupBody({
  group,
  open,
  canAct,
  query,
  ctx,
  pinned,
  selected,
  expanded,
  onToggleGroup,
  onExpand,
  onSelect,
  onPin,
  onKill,
}: {
  group: RegisterGroup;
  open: boolean;
  canAct: boolean;
  query: string;
  ctx: { breachedKeys: ReadonlySet<string> };
  pinned: ReadonlySet<string>;
  selected: readonly string[];
  expanded: string | null;
  onToggleGroup: () => void;
  onExpand: (key: string) => void;
  onSelect: (key: string, on: boolean) => void;
  onPin: (key: string) => void;
  onKill: (row: AdminExperimentRecord) => void;
}) {
  const span = spanOf(canAct);
  return (
    <tbody className={group.id === SPLITS_GROUP ? "exr__group exr__group--live" : "exr__group"}>
      <tr className="exr__group-head">
        <th scope="colgroup" colSpan={span}>
          <button type="button" className="exr__group-toggle" onClick={onToggleGroup}>
            <span aria-hidden="true">{open ? "v" : ">"}</span> {group.label}
            <span className="exr__group-count">{group.rows.length.toLocaleString()}</span>
            <span className="exr__group-states">
              {group.breakdown.map((b) => (
                <span key={b.state} className={`exr__group-state exr__group-state--${b.state}`}>
                  {b.count} {b.state}
                </span>
              ))}
            </span>
          </button>
        </th>
      </tr>
      {open
        ? group.rows.map((row) => (
            <RegisterRow
              key={`${group.id}:${row.kind}:${row.key}`}
              row={row}
              query={query}
              breached={ctx.breachedKeys.has(row.key)}
              pinned={pinned.has(row.key)}
              canAct={canAct}
              selected={selected.includes(row.key)}
              expanded={expanded === `${group.id}:${row.kind}:${row.key}`}
              onExpand={() => onExpand(`${group.id}:${row.kind}:${row.key}`)}
              onSelect={(on) => onSelect(row.key, on)}
              onPin={() => onPin(row.key)}
              onKill={() => onKill(row)}
              span={span}
            />
          ))
        : null}
    </tbody>
  );
}

function SavedViews({
  active,
  onPick,
}: {
  active: SavedView | undefined;
  onPick: (next: SavedView | undefined) => void;
}) {
  return (
    <div className="exr__views" role="group" aria-label="Saved views">
      {SAVED_VIEWS.map((view) => (
        <button
          key={view}
          type="button"
          className={`exr__view${active === view ? " exr__view--on" : ""}`}
          aria-pressed={active === view}
          onClick={() => onPick(active === view ? undefined : view)}
        >
          {SAVED_VIEW_LABELS[view]}
        </button>
      ))}
    </div>
  );
}

function FilterBar({
  filters,
  experiments,
  onChange,
  onClear,
}: {
  filters: RegisterFilters;
  experiments: readonly AdminExperimentRecord[];
  onChange: (next: Partial<RegisterFilters>) => void;
  onClear: () => void;
}) {
  const kinds = [...new Set(experiments.map((e) => e.kind))].sort();
  const units = [...new Set(experiments.map((e) => e.unit))].sort();
  // The whole vocabulary rather than only what this snapshot happens to hold,
  // so "killed" is offerable on a day nothing is killed and the reader can see
  // that the answer is none rather than that the option does not exist.
  const states = [...REGISTER_STATES];
  const owners = [...new Set(experiments.map((e) => e.owner).filter(Boolean))].sort();
  const layers = [...new Set(experiments.map((e) => e.layer).filter(Boolean))].sort();
  const agents = agentsIn(experiments);

  return (
    <div className="exr__filters">
      {/* `/` focuses the search box: the register is a keyboard surface and the
          first thing anybody does on it is look for one experiment. */}
      <input
        className="exr__search"
        type="search"
        placeholder="Search key, label, description, arm, kind, Monday id, actor (press /)"
        aria-label="Search the register"
        value={filters.q}
        data-register-search="true"
        onChange={(e) => onChange({ q: e.target.value })}
      />
      <Picker label="Kind" value={filters.kind} options={kinds} onChange={(kind) => onChange({ kind })} />
      <Picker
        label="State"
        value={filters.state}
        options={states}
        onChange={(state) => onChange({ state })}
      />
      <Picker
        label="Agent"
        value={filters.agent}
        options={agents}
        onChange={(agent) => onChange({ agent })}
      />
      <Picker label="Unit" value={filters.unit} options={units} onChange={(unit) => onChange({ unit })} />
      {owners.length > 0 ? (
        <Picker
          label="Owner"
          value={filters.owner}
          options={owners}
          onChange={(owner) => onChange({ owner })}
        />
      ) : null}
      {layers.length > 0 ? (
        <Picker
          label="Layer"
          value={filters.layer}
          options={layers}
          onChange={(layer) => onChange({ layer })}
        />
      ) : null}
      <label className="exr__toggle">
        <input
          type="checkbox"
          checked={filters.doingSomething}
          onChange={(e) => onChange({ doingSomething: e.target.checked })}
        />
        Splitting somebody only
      </label>
      <label className="exr__toggle">
        <input
          type="checkbox"
          checked={filters.hasBreaches}
          onChange={(e) => onChange({ hasBreaches: e.target.checked })}
        />
        Breached only
      </label>
      <label className="exr__toggle">
        <input
          type="checkbox"
          checked={filters.hasMonday}
          onChange={(e) => onChange({ hasMonday: e.target.checked })}
        />
        Has a Monday card
      </label>
      <label className="exr__toggle">
        Changed within
        <select
          className="exr__days"
          value={filters.changedWithinDays ?? ""}
          onChange={(e) =>
            onChange({ changedWithinDays: e.target.value ? Number(e.target.value) : null })
          }
        >
          <option value="">any time</option>
          <option value="1">1 day</option>
          <option value="7">7 days</option>
          <option value="30">30 days</option>
        </select>
      </label>
      <Button variant="ghost" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}

function Picker({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (next: string) => void;
}) {
  return (
    <label className="exr__picker">
      {label}
      <select className="exr__select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">any</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

/** A cell with the search terms marked. Pure rendering over `highlightParts`,
 *  which is where the matching rule lives and is tested. */
function Hit({ text, query }: { text: string; query: string }) {
  if (!query.trim() || text === "") return <>{text}</>;
  return (
    <>
      {highlightParts(text, query).map((part, i) =>
        part.hit ? (
          <mark className="exr__hit" key={i}>
            {part.text}
          </mark>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </>
  );
}

function RegisterRow({
  row,
  query,
  breached,
  pinned,
  canAct,
  selected,
  expanded,
  span,
  onExpand,
  onSelect,
  onPin,
  onKill,
}: {
  row: AdminExperimentRecord;
  query: string;
  breached: boolean;
  pinned: boolean;
  canAct: boolean;
  selected: boolean;
  expanded: boolean;
  span: number;
  onExpand: () => void;
  onSelect: (on: boolean) => void;
  onPin: () => void;
  onKill: () => void;
}) {
  const facts = registerFactsOf(row);
  const state = stateOf(row);
  const change = lastChangeOf(row);
  const decision = decisionOf(row);
  const exposures = totalExposuresOf(row);
  const days = daysRunningOf(row);
  const dots = facts?.guardrailDots ?? [];
  const dialable = allocatableKind(row.kind) !== null;

  return (
    <>
      <tr className={pinned ? "exr__row exr__row--pinned" : "exr__row"}>
        <td className="exr__pick">
          <button
            type="button"
            className="exr__expand"
            aria-expanded={expanded}
            aria-label={`${expanded ? "Hide" : "Show"} the control for ${row.key}`}
            onClick={onExpand}
          >
            <span aria-hidden="true">{expanded ? "v" : ">"}</span>
          </button>
        </td>
        {canAct ? (
          <td className="exr__pick">
            <input
              type="checkbox"
              checked={selected}
              aria-label={`Select ${row.key}`}
              onChange={(e) => onSelect(e.target.checked)}
            />
          </td>
        ) : null}
        <td>
          <Link className="exp-table__link" to="/experiments/$key" params={{ key: row.key }}>
            <code className="exp-table__key">
              <Hit text={row.key} query={query} />
            </code>
          </Link>
          <span className="exr__label">
            <Hit text={row.label || row.description || ""} query={query} />
          </span>
        </td>
        <td>
          <Badge tone="neutral">{row.kind}</Badge>
        </td>
        <td>{row.unit}</td>
        <td>
          <Badge tone={STATE_TONE[state] ?? "neutral"}>{state}</Badge>
          {/* Same reason as the board card: `off` describes the EXPERIMENT, and for a
              declaration that ships a non-baseline arm that reads as "nothing is
              happening" about a product that is doing the thing. See `shippedArm`. */}
          {state === "off" && shippedArm(row.defaultAllocation) ? (
            <Badge tone="accent">ships {shippedArm(row.defaultAllocation)}</Badge>
          ) : null}
          {decision ? (
            <span className="exr__decision">
              {decision.outcome} {decision.winnerArm}
            </span>
          ) : null}
        </td>
        <td>{summariseAllocation(row)}</td>
        <td className="exp-table__num">{exposures === null ? "-" : exposures.toLocaleString()}</td>
        <td className="exp-table__num">{formatCostPerCall(average(row, "costPerCallUsd"))}</td>
        <td className="exp-table__num">{formatRate(average(row, "errorRate"))}</td>
        <td className="exp-table__num">{formatLatencyMs(average(row, "p95LatencyMs"))}</td>
        <td className="exp-table__num">{days === null ? "-" : days}</td>
        <td>{row.owner || "-"}</td>
        <td>
          {/* A warning, never a gate: see `expiryState`. A kind that carries no expiry
              yet reads "-" rather than anything that implies a review happened. */}
          {row.expires ? (
            <span
              className={
                expiryState(row.expires, todayIso()) === "lapsed" ? "exr__lapsed" : undefined
              }
              title={
                expiryState(row.expires, todayIso()) === "lapsed"
                  ? "Past its declared expiry. Nothing is gated or stopped; nobody has revisited it."
                  : undefined
              }
            >
              {row.expires}
            </span>
          ) : (
            "-"
          )}
        </td>
        <td>{row.monday ? <Hit text={row.monday} query={query} /> : "-"}</td>
        <td>
          {change ? (
            <>
              <span className="exr__actor">
                <Hit text={change.actorEmail} query={query} />
              </span>
              <span className="exr__when">{formatDateTime(change.at)}</span>
            </>
          ) : (
            "-"
          )}
        </td>
        <td>
          {dots.length === 0 ? (
            <span className="exr__dots exr__dots--none" title="Nothing measured in the window">
              -
            </span>
          ) : (
            <span className="exr__dots">
              {dots.map((d) => (
                <span
                  key={`${d.arm}:${d.guardrail}`}
                  className={`exr__dot exr__dot--${d.status}`}
                  title={`${d.arm}: ${d.guardrail} (${d.signal}) ${d.status}`}
                />
              ))}
            </span>
          )}
          {breached && dots.length === 0 ? <Badge tone="danger">breach</Badge> : null}
        </td>
        <td className="exr__row-actions">
          <Link className="ad-link" to="/experiments/$key" params={{ key: row.key }}>
            Open
          </Link>
          <button type="button" className="exr__mini" onClick={onPin}>
            {pinned ? "Unpin" : "Pin me"}
          </button>
          <button
            type="button"
            className="exr__mini"
            onClick={() => void navigator.clipboard?.writeText(row.key)}
          >
            Copy key
          </button>
          {/* Killable for every kind the console can allocate, which is now all
              six. `off` is the one state with nothing to stop. */}
          {canAct && dialable && state !== "off" ? (
            <button type="button" className="exr__mini exr__mini--danger" onClick={onKill}>
              Kill
            </button>
          ) : null}
        </td>
      </tr>
      {expanded ? (
        <tr className="exr__control">
          <td colSpan={span}>
            {/* The control INSIDE the panel that says what the thing is. A
                slider on its own asks an operator to move traffic between two
                arms they cannot read, which is how a second approach shipped
                identical text to the first for twenty-five slots. */}
            <ExperimentInspector
              experimentKey={row.key}
              kind={row.kind}
              onClose={onExpand}
            >
              <AllocationEditor record={row} canAct={canAct} compact />
            </ExperimentInspector>
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** One numeric column, averaged over the arms that were measured. Null when no
 *  arm was, so the cell prints "-" rather than a confident zero. */
function average(
  row: AdminExperimentRecord,
  field: "costPerCallUsd" | "errorRate" | "p95LatencyMs",
): number | null {
  const values = row.strips
    .map((s) => s[field])
    .filter((v): v is number => v !== null && v !== undefined);
  return values.length === 0 ? null : values.reduce((n, v) => n + v, 0) / values.length;
}

/** The reason prompt every kill goes through, single or bulk. Two steps on
 *  purpose: killing moves live traffic, and the reason is the only field that
 *  will still make sense to somebody reading the audit table in three months. */
function KillPanel({
  rows,
  onCancel,
  onConfirm,
}: {
  rows: readonly AdminExperimentRecord[];
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = reason.trim().length >= 3;

  return (
    <div className="exr__kill" role="group" aria-label="Kill these experiments">
      <p className="exr__kill-what">
        Stop {rows.length === 1 ? <code>{rows[0].key}</code> : `${rows.length} experiments`}: every
        non-baseline arm goes to zero. The code stays deployed and inert, so this is reversible
        by moving the dial back up.
      </p>
      {rows.length > 1 ? (
        <ul className="exr__kill-list">
          {rows.map((r) => (
            <li key={r.key}>
              <code>{r.key}</code>
            </li>
          ))}
        </ul>
      ) : null}
      <input
        className="exr__kill-reason"
        value={reason}
        autoFocus
        placeholder="Why (this lands on the audit row)"
        aria-label="Reason"
        onChange={(e) => setReason(e.target.value)}
      />
      {error ? <p className="exp-section__error">{error}</p> : null}
      <div className="exr__kill-actions">
        <Button
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await onConfirm(reason.trim());
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            } finally {
              setBusy(false);
            }
          }}
          disabled={!ready || busy}
        >
          {busy ? "Stopping" : "Kill"}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** The export is built in the browser from the rows already on screen, so it
 *  matches the board exactly and costs the backend nothing. */
function downloadCsv(csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = registerCsvFilename(new Date());
  link.click();
  URL.revokeObjectURL(url);
}
